import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import useStudentExit from "../hooks/useStudentExit";
import StudentExitTable from "../components/StudentExitTable";
import StudentSelectorWrapper from "components/Peoples/guardian/StudentSelectorWrapper";
import { useEffectiveStudentId } from "components/Peoples/guardian/hooks/useEffectiveStudentId";
import StudentExitPeriods from "../components/StudentExitPeriods";
import AddEditStudentExit from "../components/AddEditStudentExit";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import GuardianStudentExitForm from "../components/GuardianStudentExitForm";
import StudentExitStudentTable from "../components/StudentExitStudentTable";

const MyStudentExitPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    studentExits,
    loading,
    fetchStudentExits,
    infoPagination,
    statusOptions,
    exitTypeOptions,
    handleCreate,
    handleUpdate,
    handleGetGuardianByStudentId,
    handleGetAuthorizedByStudentId,
    handleDelete,
  } = useStudentExit();
  const studentId = useEffectiveStudentId();
  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});
  const students = useSelector((state: RootState) => state.student.students);
  const stundet = useMemo(() => students.find((student: any) => student.id === studentId), [students, studentId]);

  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    { label: "studentExit.title", active: true },
  ];

  const filterFields: FilterField[] = useMemo(
    () => [
      { key: "search", label: t("search"), type: "text" },
      { key: "status", label: t("studentExit.form.status"), type: "select", options: statusOptions },
      { key: "exitType", label: t("studentExit.form.exitType"), type: "select", options: exitTypeOptions },
      { key: "exitTime", label: t("studentExit.form.exitTime"), type: "range", rangeKeys: { start: "startDate", end: "endDate" } },
    ],
    [t, statusOptions, exitTypeOptions]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudentExit, setSelectedStudentExit] = useState<any>(null);


  const handleFilter = (newFilters: { [key: string]: any }) => {
    setFilters(newFilters);
    refetchData(newFilters);
  };

  const refetchData = (currentFilters = filters) => {
    const params = {
      ...currentFilters,
      studentId,
      page: 1,
      limit: 10,
    };

    fetchStudentExits(params);
  };



  const handleSave = async (payload: any) => {
    try {
      let result = false;

      if (selectedStudentExit?.id) {
        result = await handleUpdate(selectedStudentExit.id, payload);
      } else {
        result = await handleCreate(payload);
      }

      if (result) {
        setSelectedStudentExit(null);
        refetchData();
      }
      return result;
    } catch (error) {
      console.error("Error saving student exit:", error);
      return false;
    }
  };

  useEffect(() => {
    fetchStudentExits({ studentId, page: 1, limit: 10 });
  }, [studentId]);

  const handleAddNew = (period: string , lastSession: any) => {
    setSelectedStudentExit({ exitType: period ,exitTime : lastSession.endTime });
    setIsModalOpen(true);
  };

  const handleDeleteStudentExit = async (id: string) => {
    const result = await handleDelete(id);
    if (result) {
      refetchData();
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("studentExit.title")} breadcrumbItems={breadcrumbItems} />
        <StudentSelectorWrapper rightSlot={<TableFilterDynamique
          fields={filterFields}
          filters={filters}
          onApply={handleFilter}
          onReset={() => handleFilter({ page: 1, limit: 10 })}
        />}>
          <>
            <StudentExitPeriods
              handleAddNew={handleAddNew}
            />
            <StudentExitStudentTable
              dataSource={studentExits}
              loading={loading}
              dataPaginate={{
                current: infoPagination?.page,
                pageSize: infoPagination?.limit,
                total: infoPagination?.total,
              }}
              onChange={(pg) => {
                const params = {
                  ...filters,
                  studentId,
                  page: pg.current!,
                  limit: pg.pageSize!,
                };
                fetchStudentExits(params);
              }}
              onDelete={handleDeleteStudentExit}
            />

            <GuardianStudentExitForm
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={handleSave}
              studentId={studentId}
              exitType={selectedStudentExit?.exitType}
              exitTime={selectedStudentExit?.exitTime}
              onGetGuardianByStudentId={handleGetGuardianByStudentId}
              onGetAuthorizedByStudentId={handleGetAuthorizedByStudentId}
            />
          </>
        </StudentSelectorWrapper>

      </div>
    </div>
  );
};

export default MyStudentExitPage;